import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { MutableRefObject, useEffect, useLayoutEffect, useMemo, useRef } from 'react'

// Threshold for detecting unexpected scroll position changes (in pixels)
const SCROLL_POSITION_TOLERANCE = 10

// Polling configuration for finding the virtualScroller element
const POLL_INTERVAL_MS = 50
const MAX_POLL_ATTEMPTS = 40 // 2 seconds total

interface UsePreserveScrollPositionProps {
  apiRef: MutableRefObject<GridApiPremium>
  isUsingDataSource: boolean
  rows: readonly unknown[]
  stateKey?: string | null
}

/**
 * Preserves scroll position across re-renders by:
 * 1. Tracking scroll position on user scroll events
 * 2. Restoring position synchronously after re-renders if it changes unexpectedly
 *
 * This prevents MUI DataGrid's virtualization from resetting scroll position
 * when parent components re-render. This caused a number of issues - see tickets
 * attached to this PR/commit
 */
export const usePreserveScrollPosition = ({ apiRef, isUsingDataSource, rows, stateKey }: UsePreserveScrollPositionProps) => {
  const scrollPosRef = useRef({ top: 0, left: 0 })
  const scrollerRef = useRef<HTMLElement | null>(null)
  const hasScrolledRef = useRef(false)

  // v1 tables may render their virtualScroller only once rows arrive, so we re-run
  // polling when rows changes to catch a late render. v2 (data-source) tables never
  // push rows through props, so depending on the prop would either thrash on each
  // render or provide no useful retry signal — skip the dep entirely.
  const rowsRetrigger = useMemo(() => (isUsingDataSource ? null : rows), [isUsingDataSource, rows])

  // Save scroll position on every scroll event - poll until element is available
  useEffect(() => {
    if (apiRef.current === undefined) {
      return
    }

    let cleanupFn: (() => void) | undefined
    let isSetup = false

    const setupScrollListener = (): boolean => {
      if (isSetup) return true

      const rootElement = apiRef.current?.rootElementRef?.current ?? null
      if (rootElement === null) return false

      const virtualScroller = rootElement.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null
      if (virtualScroller === null) return false

      scrollerRef.current = virtualScroller
      isSetup = true

      const handleScroll = () => {
        hasScrolledRef.current = true
        scrollPosRef.current = {
          top: virtualScroller.scrollTop,
          left: virtualScroller.scrollLeft,
        }
      }

      virtualScroller.addEventListener('scroll', handleScroll, { passive: true })
      cleanupFn = () => virtualScroller.removeEventListener('scroll', handleScroll)

      return true
    }

    // Poll every 50ms until element is found (max 40 attempts = 2 seconds)
    let attempts = 0
    const pollInterval = setInterval(() => {
      const found = setupScrollListener()
      attempts++

      if (found === true || attempts >= MAX_POLL_ATTEMPTS) {
        clearInterval(pollInterval)
      }
    }, POLL_INTERVAL_MS)

    return () => {
      clearInterval(pollInterval)
      cleanupFn?.()
    }
  }, [apiRef, stateKey, rowsRetrigger])

  // Restore scroll position synchronously after each render using layout effect
  useLayoutEffect(() => {
    const virtualScroller = scrollerRef.current

    // Only restore if we've captured a scroll position before
    if (!virtualScroller || !hasScrolledRef.current) return

    const currentTop = virtualScroller.scrollTop
    const currentLeft = virtualScroller.scrollLeft

    // If scroll position changed unexpectedly, restore it
    if (
      Math.abs(currentTop - scrollPosRef.current.top) > SCROLL_POSITION_TOLERANCE ||
      Math.abs(currentLeft - scrollPosRef.current.left) > SCROLL_POSITION_TOLERANCE
    ) {
      virtualScroller.scrollTop = scrollPosRef.current.top
      virtualScroller.scrollLeft = scrollPosRef.current.left
    }
  })
}
